import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/http/base-http.service';
import { UrlParam } from '@shared/enums/url-param.enum';
import { MetaInformation } from '@shared/interfaces/attribute-description.interface';
import { EventInterface } from '@shared/interfaces/event.interface';
import { GroupInterface } from '@shared/interfaces/group.interface';
import { JoinInRequestInterface } from '@shared/interfaces/join.interface';
import { NewsMessageInterface } from '@shared/interfaces/news-message.interface';
import { ApiGetResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import { map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GroupHttpService extends BaseHttpService<GroupInterface> {
  constructor(protected http: HttpClient) {
    super(http);
  }

  create(
    dto: GroupInterface & { entityCreateFromOrgId?: number },
    params: any = {}
  ): Observable<GroupInterface> {
    if (!dto) {
      return of(null);
    }

    if (dto.entityCreateFromOrgId) {
      params = (params || new HttpParams()).set(
        UrlParam.FilterTokenId,
        String(dto.entityCreateFromOrgId)
      );
    }

    let url = `${this.apiUrl}`;
    url = url.replace(/\/$/, ''); // Remove trailing slash

    return this.http.post<GroupInterface>(url, dto, { params });
  }

  getAttributeDescription(params): Observable<MetaInformation> {
    return super.getAttributeDescription(params).pipe(
      map(data => {
        return data;
      })
    );
  }

  getApiRootPath(): string {
    return 'groups';
  }

  gets(params = null): Observable<GroupInterface[]> {
    return super.gets(params).pipe(
      map((pagedList: unknown) => {
        if (!pagedList['items']) {
          return [];
        }

        return pagedList['items'].map((group: GroupInterface) =>
          this.tranformData(group)
        );
      })
    );
  }

  read(
    id: number,
    headers?: HttpHeaders,
    params?: any
  ): Observable<GroupInterface> {
    return super
      .read(id, headers, params)
      .pipe(map(group => this.tranformData(group)));
  }

  export(
    params: any = null,
    _innerPath: string | null = null,
    _queryParam = null
  ) {
    if (params) {
      const url = `${this.apiUrl}/exports`;

      return this.http.post(url, params, {
        responseType: 'blob',
        observe: 'response',
      });
    }
  }

  paginate(
    params: any,
    innerPath: string | null = null
  ): Observable<ApiGetResponse<GroupInterface>> {
    return super.paginate(params, innerPath).pipe(
      map((res: ApiGetResponse<GroupInterface>) => {
        return super.handlePaginateResponse(res);
      })
    );
  }

  paginateX(
    params: any,
    innerPath: string | null = null
  ): Observable<ApiGetResponse<GroupInterface>> {
    return super.paginateX(params, innerPath).pipe(
      map((res: ApiGetResponse<GroupInterface>) => {
        return super.handlePaginateResponse(res);
      })
    );
  }

  checkPartnerOrganizationTeam(orgChallengeId: number): Observable<boolean> {
    if (orgChallengeId && !Number.isNaN(orgChallengeId)) {
      const url = `${this.apiUrl}/check-partner-organization-team`;

      return this.http.get<boolean>(url, {
        params: { id: orgChallengeId.toString() },
      });
    }

    return of(false);
  }

  paginateAsPost(
    params = null,
    innerPath: string | null = null,
    queryParam = null
  ): Observable<ApiGetResponse<GroupInterface>> {
    return super.paginateAsPost(params, innerPath, queryParam).pipe(
      map((res: ApiGetResponse<GroupInterface>) => {
        return this.handlePaginateResponse(res);
      })
    );
  }

  join(id: number, dto: JoinInRequestInterface): Observable<void> {
    if (id) {
      const url = `${this.apiUrl}/${id}/join`;
      return this.http.post<void>(url, dto);
    } else {
      return of(null);
    }
  }

  deactivateGroup(id) {
    const url = `${this.apiUrl}/${id}/deactivate`;

    return this.http.delete(url);
  }

  paginateNews(params) {
    const url = `${this.apiUrl}/news`;

    return this.http.get(url, { params }).pipe(
      map((res: ApiGetResponse<any>) => {
        return this.handlePaginateResponse(
          res
        ) as unknown as ApiGetResponse<NewsMessageInterface>;
      })
    );
  }

  updateGroupCommunity(id: number, payload: Record<string, number[]>) {
    const url = `${this.apiUrl}/${id}/update-group-community`;
    return this.http.post(url, payload);
  }

  paginateEvents(
    groupId: number,
    params
  ): Observable<ApiGetResponse<EventInterface>> {
    const url = `${this.apiUrl}/${groupId}/events`;

    return this.http.get<ApiGetResponse<EventInterface>>(url, { params });
  }
}
